<template>
  <div class="px-2 py-4 sm:px-4 sm:py-6">
    <!-- Status filter tabs with controls -->
    <PageTabs v-model="selectedStatus" :tabs="displayTabs" :controls-first="true">
      <template #controls>
        <DoenEmployeeFilter v-model="selectedEmployee" :employees="employees" />
      </template>

      <template #empty>
        <div class="flex items-center gap-2">
          <span
            v-if="filteredActions?.length > 0"
            class="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 py-0.5 px-1.5 sm:px-2 rounded-full text-[10px] sm:text-xs min-w-[1.5rem] sm:min-w-[2rem] inline-block text-center"
          >
            {{ filteredActions.length }}
          </span>
        </div>
      </template>
    </PageTabs>

    <!-- Content container with minimum height to prevent layout shifts -->
    <div class="min-h-[400px] mt-6">
      <DoenActionList
        :actions="filteredActions"
        :is-loading="isLoading"
        :error="error"
        :expanded-actions="expandedActions"
        :is-updating-action="isUpdatingAction"
        :is-admin="isAdmin"
        :empty-heading="emptyHeading"
        :empty-description="emptyDescription"
        @toggle-expand="toggleExpand"
        @deal="handleDeal"
        @no-deal="handleNoDeal"
        @confirm-create-project="handleConfirmAndCreateProject"
        @pl-no-match="handlePlNoMatch"
        @complete="markAsCompleted"
        @delete="deleteAction"
        @update-deadline="handleUpdateDeadline"
      />
    </div>
  </div>

  <!-- Delete action confirmation modal -->
  <ConfirmActionModal
    :open="showDeleteActionConfirm"
    title="Actie verwijderen"
    :message="
      actionToDelete
        ? `Weet je zeker dat je de actie &quot;${actionToDelete.action_type_name || 'Actie'}&quot; voor ${actionToDelete.organization_name} wilt verwijderen?`
        : ''
    "
    confirm-label="Verwijderen"
    variant="danger"
    @confirm="confirmDeleteAction"
    @cancel="showDeleteActionConfirm = false"
  />
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import ConfirmActionModal from '@/components/common/modals/ConfirmActionModal.vue'
import { actionsService } from '@/services/actionsService.js'
import { workflowService } from '@/services/workflowService.js'
import { toast } from '@/composables/ui/useToast'
import PageTabs from '@/components/ui/PageTabs.vue'
import { useCurrentUser } from '@/composables/auth/useCurrentUser'
import { useAuth } from '@/composables/auth/useAuth'
import { invalidateNavBadges } from '@/composables/ui/useNavBadges'
import { ACTION_STATUS, getStatusLabel } from '@/constants/actions'
import DoenEmployeeFilter from '@/components/doen/DoenEmployeeFilter.vue'
import DoenActionList from '@/components/doen/DoenActionList.vue'

// Current user composable
const { getApiUserInfo } = useCurrentUser()
const { isAdmin } = useAuth()
const queryClient = useQueryClient()

// State
const selectedStatus = ref('active')
const expandedActions = ref({})
const isUpdatingAction = ref({})
const selectedEmployee = ref(null)

// Special "Alles" option to show actions for all employees
const ALL_OPTION = { simplicate_id: '__ALL__', name: 'Alles' }

// Status tabs
const statusTabs = [
  { value: 'active', label: 'Openstaand' },
  { value: 'completed', label: 'Afgerond' },
  { value: 'all', label: 'Alles' }
]

// Always show the status tabs, including when "Alles" is selected.
const displayTabs = computed(() => {
  return statusTabs.map(tab => ({
    key: tab.value,
    label: tab.label,
    count: getCountByStatus(tab.value) > 0 ? getCountByStatus(tab.value) : undefined
  }))
})

// Get current user info
const getCurrentUser = () => getApiUserInfo()

// Fetch employees with actions via TanStack Query (shared with useNavBadges)
const { data: employeesData } = useQuery({
  queryKey: ['employees-with-actions'],
  queryFn: () => actionsService.getEmployeesWithActions(),
  staleTime: 1000 * 60 * 5
})

const employees = computed(() => employeesData.value || [])

// Auto-match current user when employees data loads
watch(
  employees,
  newEmployees => {
    if (newEmployees.length === 0 || selectedEmployee.value) return

    const currentUser = getCurrentUser()
    if (currentUser.name) {
      const matchedEmployee = newEmployees.find(
        e => e.name.toLowerCase() === currentUser.name.toLowerCase()
      )
      if (matchedEmployee) {
        selectedEmployee.value = matchedEmployee
        localStorage.setItem('my_employee_id', matchedEmployee.simplicate_id)
        return
      }
    }

    const savedEmployeeId = localStorage.getItem('my_employee_id')
    if (savedEmployeeId) {
      if (savedEmployeeId === '__ALL__') {
        selectedEmployee.value = ALL_OPTION
        return
      }
      const savedEmployee = newEmployees.find(e => e.simplicate_id === savedEmployeeId)
      if (savedEmployee) {
        selectedEmployee.value = savedEmployee
        return
      }
    }

    selectedEmployee.value = ALL_OPTION
  },
  { immediate: true }
)

// Watch for employee selection changes and save to localStorage
watch(selectedEmployee, newEmployee => {
  if (newEmployee) {
    localStorage.setItem('my_employee_id', newEmployee.simplicate_id)
  }
})

// Fetch actions
const {
  data: actions,
  isLoading,
  error,
  refetch
} = useQuery({
  queryKey: [
    'my-actions',
    computed(() => selectedEmployee.value?.simplicate_id),
    computed(() =>
      selectedEmployee.value?.simplicate_id === '__ALL__' ? selectedStatus.value : null
    )
  ],
  queryFn: async () => {
    if (!selectedEmployee.value?.simplicate_id) return []
    const isAllSelected = selectedEmployee.value.simplicate_id === '__ALL__'
    const employeeId = isAllSelected ? null : selectedEmployee.value.simplicate_id
    const status = isAllSelected ? selectedStatus.value : null
    // When a specific employee is selected, fetch all their actions (high limit).
    // For "Alles", the selected status tab is sent to the API; "all" means no filter.
    const params = { ...(isAllSelected ? { status } : {}) }
    if (employeeId) {
      params.limit = 200
    }
    return actionsService.getMyActions(employeeId, params)
  },
  staleTime: 1000 * 60 * 2,
  enabled: computed(() => !!selectedEmployee.value?.simplicate_id),
  refetchOnMount: true
})

// Filtered actions based on selected status
const filteredActions = computed(() => {
  if (!actions.value) return []
  if (selectedStatus.value === 'all') return actions.value
  if (selectedStatus.value === 'active') {
    return actions.value.filter(a => a.status === 'open' || a.status === 'in_progress')
  }
  return actions.value.filter(a => a.status === selectedStatus.value)
})

// Get count by status
const getCountByStatus = status => {
  if (!actions.value) return 0
  if (status === 'all') return actions.value.length
  if (status === 'active') {
    return actions.value.filter(a => a.status === 'open' || a.status === 'in_progress').length
  }
  return actions.value.filter(a => a.status === status).length
}

// Empty state heading/description
const emptyHeading = computed(() => {
  if (selectedEmployee.value?.simplicate_id === '__ALL__') {
    if (selectedStatus.value === 'all') return 'Geen acties gevonden'
    return selectedStatus.value === 'completed'
      ? 'Geen afgeronde acties'
      : 'Geen openstaande acties'
  }
  if (selectedStatus.value === 'all') return 'Geen acties gevonden'
  return `Geen ${getStatusLabel(selectedStatus.value).toLowerCase()} acties`
})

const emptyDescription = computed(() => {
  if (selectedEmployee.value?.simplicate_id === '__ALL__') {
    if (selectedStatus.value === 'all') return 'Er zijn momenteel geen acties.'
    return selectedStatus.value === 'completed'
      ? 'Er zijn momenteel geen afgeronde acties.'
      : 'Er zijn momenteel geen openstaande acties.'
  }
  if (selectedStatus.value === 'all') return 'Er zijn momenteel geen acties aan jou toegewezen.'
  return 'Er zijn geen acties met deze status.'
})

// Toggle expand
const toggleExpand = actionId => {
  expandedActions.value[actionId] = !expandedActions.value[actionId]
}

// Actions can change both the Doen list and the prospect data used by
// Concretiseren. Invalidate all related caches so a cached prospect cannot
// keep showing an open action or an old workflow status after a transition.
const invalidateActionRelatedQueries = () =>
  Promise.all(
    [['my-actions'], ['employees-with-actions'], ['prospects-leads']].map(queryKey =>
      queryClient.invalidateQueries({ queryKey })
    )
  )

// Handle Deal outcome
const handleDeal = async action => {
  isUpdatingAction.value[action.id] = true
  try {
    const user = getCurrentUser()
    await actionsService.setOutcome(action.id, 'deal', user, null)
    await refetch()
    await invalidateActionRelatedQueries()
    invalidateNavBadges(queryClient, { doen: true })
    toast.success(
      'Deal geregistreerd. PL wordt op de hoogte gebracht om beschikbaarheid te checken.',
      { title: 'Deal bevestigd' }
    )
  } catch (error) {
    console.error('Error setting deal outcome:', error)
    toast.error('Fout bij registreren van deal. Probeer het opnieuw.')
  } finally {
    isUpdatingAction.value[action.id] = false
  }
}

// Handle PL confirmation: trigger project_maken follow-up and complete the action
const handleConfirmAndCreateProject = async action => {
  isUpdatingAction.value[action.id] = true
  try {
    const user = getCurrentUser()
    await actionsService.triggerFollowup(action.id, user)
    await actionsService.updateStatus(action.id, ACTION_STATUS.COMPLETED, {
      completed_by_name: user.name,
      completed_by_email: user.email
    })
    await refetch()
    await invalidateActionRelatedQueries()
    invalidateNavBadges(queryClient, { doen: true })
    queryClient.invalidateQueries({ queryKey: ['weekly-availability'] })
    toast.success('Actie "Project maken" is aangemaakt.', { title: 'Beschikbaarheid bevestigd' })
  } catch (error) {
    console.error('Error confirming and creating project:', error)
    toast.error('Fout bij aanmaken van project actie. Probeer het opnieuw.')
  } finally {
    isUpdatingAction.value[action.id] = false
  }
}

// Handle PL "Geen match"
const handlePlNoMatch = async action => {
  const confirmed = window.confirm(
    "Er moeten nieuwe collega's worden voorgedragen voor dit project. Je vindt deze kans terug op de beoordelen pagina zodat er een nieuw voorstel kan worden ingediend."
  )
  if (!confirmed) return

  isUpdatingAction.value[action.id] = true
  try {
    const user = getCurrentUser()
    await workflowService.reassess(
      action.prospect_id,
      user,
      'Geen match — PL heeft geen beschikbare medewerker'
    )
    await refetch()
    await invalidateActionRelatedQueries()
    await queryClient.invalidateQueries({ queryKey: ['weekly-availability'] })
    invalidateNavBadges(queryClient, { doen: true, beoordelen: true, concretiseren: true })
    toast.success('De kans is teruggezet naar de beoordelen pagina.', { title: 'Geen match' })
  } catch (error) {
    console.error('Error reassessing prospect:', error)
    toast.error('Fout bij terugzetten naar beoordelen. Probeer het opnieuw.')
  } finally {
    isUpdatingAction.value[action.id] = false
  }
}

// Handle No Deal outcome
const handleNoDeal = async action => {
  const notes = prompt('Reden voor No Deal (optioneel):')
  if (notes === null) return
  isUpdatingAction.value[action.id] = true
  try {
    const user = getCurrentUser()
    await actionsService.setOutcome(action.id, 'no_deal', user, notes)
    await refetch()
    await invalidateActionRelatedQueries()
    await queryClient.invalidateQueries({ queryKey: ['weekly-availability'] })
    invalidateNavBadges(queryClient, { doen: true })
    toast.info('No Deal geregistreerd.', { title: 'Status bijgewerkt' })
  } catch (error) {
    console.error('Error setting no deal outcome:', error)
    toast.error('Fout bij registreren van no deal. Probeer het opnieuw.')
  } finally {
    isUpdatingAction.value[action.id] = false
  }
}

// Mark action as completed
const markAsCompleted = async action => {
  isUpdatingAction.value[action.id] = true
  try {
    const user = getCurrentUser()
    await actionsService.updateStatus(action.id, ACTION_STATUS.COMPLETED, {
      completed_by_name: user.name,
      completed_by_email: user.email
    })
    await refetch()
    await invalidateActionRelatedQueries()
    invalidateNavBadges(queryClient, { doen: true })
    queryClient.invalidateQueries({ queryKey: ['weekly-availability'] })
    toast.success('Actie afgerond!', { title: 'Voltooid' })
  } catch (error) {
    console.error('Error completing action:', error)
    toast.error('Fout bij afronden van actie.')
  } finally {
    isUpdatingAction.value[action.id] = false
  }
}

// Update deadline (admin only)
const handleUpdateDeadline = async ({ id, due_date }) => {
  isUpdatingAction.value[id] = true
  try {
    await actionsService.update(id, { due_date })
    await refetch()
    invalidateNavBadges(queryClient, { doen: true })
    toast.success('Deadline aangepast', { title: 'Bijgewerkt' })
  } catch (error) {
    console.error('Error updating deadline:', error)
    const msg =
      error.response?.status === 403
        ? 'Geen rechten om deadline aan te passen.'
        : 'Fout bij aanpassen van deadline.'
    toast.error(msg)
  } finally {
    isUpdatingAction.value[id] = false
  }
}

// Delete action (admin only, soft-delete)
const showDeleteActionConfirm = ref(false)
const actionToDelete = ref(null)

const deleteAction = action => {
  actionToDelete.value = action
  showDeleteActionConfirm.value = true
}

const confirmDeleteAction = async () => {
  showDeleteActionConfirm.value = false
  const action = actionToDelete.value
  if (!action) return
  actionToDelete.value = null

  isUpdatingAction.value[action.id] = true
  try {
    await actionsService.delete(action.id)
    await refetch()
    queryClient.invalidateQueries({ queryKey: ['prospects-leads'] })
    invalidateNavBadges(queryClient, { doen: true, concretiseren: true })
    toast.success('Actie verwijderd', { title: 'Verwijderd' })
  } catch (error) {
    console.error('Error deleting action:', error)
    toast.error('Fout bij verwijderen van actie.')
  } finally {
    isUpdatingAction.value[action.id] = false
  }
}
</script>
